import React, {createContext, ReactNode, useEffect, useState} from "react";
import {useForm, UseFormReturn} from "react-hook-form";
import {specialistFormSchema, SpecialistFormSchemaType} from "../constants/specialist-form-schema";
import {yupResolver} from "@hookform/resolvers/yup";
import {useSpecialistApi} from "../../core/hooks/use-specialist-api";
import {SpecialistDTO} from "../../core/models/interfaces/specialist-dto.interface";
import {UpdateSpecialistRequest} from "../../core/models/interfaces/update-specialist-request.interface";
import {useModal} from "@common/hooks/use-modal";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import FileHelper from "@utils/file-helper";

// Funkcja do konwersji numeru telefonu z bazy danych (xxxxxxxxx) na format wyświetlania (xxx-xxx-xxx)
const formatPhoneForDisplay = (phoneNumber: string): string => {
    if (!phoneNumber) return '';
    // Jeśli już ma format xxx-xxx-xxx, zwróć jak jest
    if (phoneNumber.includes('-')) return phoneNumber;
    // Jeśli to 9 cyfr, sformatuj do xxx-xxx-xxx
    if (phoneNumber.length === 9 && /^\d{9}$/.test(phoneNumber)) {
        return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
    }
    return phoneNumber;
};

interface SpecialistFormContextType {
    form: UseFormReturn<SpecialistFormSchemaType>;
    formData: SpecialistFormSchemaType;
    isEditMode: boolean;
    isLoading: boolean;
    specialistId?: string;
    loadSpecialist: (id: string) => Promise<void>;
    saveSpecialist: () => Promise<void>;
    resetForm: () => void;
}

const SpecialistFormContext = createContext<SpecialistFormContextType | undefined>(undefined);

interface SpecialistFormProviderProps {
    children: ReactNode;
    specialistId?: string;
}

export const SpecialistFormProvider: React.FC<SpecialistFormProviderProps> = (props) => {
    const {
        children,
        specialistId
    } = props;

    const [isEditMode, setIsEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [currentSpecialistId, setCurrentSpecialistId] = useState<string | undefined>(specialistId);
    const [originalImage, setOriginalImage] = useState<File | null>(null);
    const {closeModal} = useModal();

    const {
        createSpecialist,
        updateSpecialist,
        getSpecialist,
        updateSpecialistProfileImage
    } = useSpecialistApi();

    const form = useForm<SpecialistFormSchemaType>({
        resolver: yupResolver(specialistFormSchema) as any,
        defaultValues: {
            id: '',
            fullName: '',
            phoneNumber: '',
            specialization: '',
            websiteUrl: '',
            znanyLekarzUrl: '',
            image: undefined
        },
        mode: "onChange",
    });

    const formData = form.getValues();

    const hasImageChanged = async (newImage: File | null, originalImage: File | null): Promise<boolean> => {
        if (!newImage && !originalImage) return false;
        if (!newImage || !originalImage) return true;

        try {
            return !(await FileHelper.compareFilesByHash(newImage,originalImage));
        } catch (error) {
            console.error('Error comparing images:', error);
            return true;
        }
    };

    const loadSpecialist = async (id: string): Promise<void> => {
        try {
            setIsLoading(true);
            setIsEditMode(true);
            setCurrentSpecialistId(id);

            const specialist: SpecialistDTO = await getSpecialist(id);

            const formData: any = {
                id: specialist.id || '',
                fullName: specialist.fullName || '',
                phoneNumber: specialist.phoneNumber ? formatPhoneForDisplay(specialist.phoneNumber) : '',
                specialization: specialist.specialization || '',
                websiteUrl: specialist.websiteUrl || '',
                znanyLekarzUrl: specialist.znanyLekarzUrl || '',
                image: undefined
            };

            if (specialist.image.base64) {
                try {
                    const {name, base64,extension} = specialist.image;
                    const file = FileHelper.convertBase64ToFile(base64!,name!,extension!);
                    formData.image = file;
                    setOriginalImage(file);
                } catch (error) {
                    console.error('Error creating file from base64:', error);
                }
            } else {
                setOriginalImage(null);
            }

            form.reset(formData);
        } catch (error) {
            console.error("❌ Error loading specialist:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const saveSpecialist = async (): Promise<void> => {
        try {
            setIsLoading(true);
            const data = form.getValues();
            let imageChanged = false;

            if (isEditMode && currentSpecialistId) {
                const {id, image, ...updateData} = data;

                if (updateData.phoneNumber) {
                    updateData.phoneNumber = updateData.phoneNumber.replace(/\D/g, '');
                }

                const updateRequest: UpdateSpecialistRequest = {
                    id: currentSpecialistId,
                    ...updateData
                };

                await updateSpecialist(updateRequest);

                const currentImage = image && (image instanceof File || image instanceof FileList)
                    ? (image instanceof FileList ? image[0] : image)
                    : null;

                imageChanged = await hasImageChanged(currentImage, originalImage);

                if (imageChanged && currentImage) {
                    await updateSpecialistProfileImage(currentSpecialistId, currentImage);
                } else {
                    console.log('No image change detected or no image to update');
                }
            } else {
                const {id, image, ...createData} = data;

                if (createData.phoneNumber) {
                    createData.phoneNumber = createData.phoneNumber.replace(/\D/g, '');
                }

                const imageFile = image instanceof FileList ? image[0] : image;
                await createSpecialist(createData, imageFile as any);
            }
            try {
                window.dispatchEvent(new CustomEvent('specialists:refresh'));
                if (isEditMode && currentSpecialistId && imageChanged) {
                    window.dispatchEvent(new CustomEvent('specialist:image-updated', {
                        detail: {specialistId: currentSpecialistId}
                    }));
                }
            } catch (e) {
                // ignore if window not available
            }
            closeModal(ModalIds.SPECIALIST_FORM);
        } catch (error) {
            console.error("❌ Error saving specialist:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = (): void => {
        form.reset({
            id: '',
            fullName: '',
            phoneNumber: '',
            specialization: '',
            websiteUrl: '',
            znanyLekarzUrl: '',
            image: undefined
        });
        setIsEditMode(false);
        setCurrentSpecialistId(undefined);
        setOriginalImage(null);
    };

    useEffect(() => {
        if (specialistId) {
            loadSpecialist(specialistId);
        }
    }, [specialistId]);

    const contextValue: SpecialistFormContextType = {
        form,
        formData,
        isEditMode,
        isLoading,
        specialistId: currentSpecialistId,
        loadSpecialist,
        saveSpecialist,
        resetForm
    };

    return (
        <SpecialistFormContext.Provider value={contextValue}>
            {children}
        </SpecialistFormContext.Provider>
    );
};

export {SpecialistFormContext};
